// Core entities
export interface Payee {
  id: string;
  name: string;
  description: string;
  category: string;
  createdAt: string;
  updatedAt: string;
}

export interface Payer {
  id: string;
  name: string;
  description: string;
  category: string;
  createdAt: string;
  updatedAt: string;
}

export interface BankAccount {
  id: string;
  name: string;
  initialBalance: number;
  currentBalance: number;
  isVault?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentMethod {
  id: string;
  description: string;
  bankAccountId: string;
  isForPayment: boolean;
  isForPayout: boolean;
  createdAt: string;
  updatedAt: string;
}

// Requests for basic entities
export interface CreatePayeeRequest {
  name: string;
  description: string;
  category: string;
}

export interface UpdatePayeeRequest {
  name: string;
  description: string;
  category: string;
}

export interface CreatePayerRequest {
  name: string;
  description: string;
  category: string;
}

export interface UpdatePayerRequest {
  name: string;
  description: string;
  category: string;
}

export interface CreateBankAccountRequest {
  name: string;
  initialBalance: number;
  isVault?: boolean;
}

export interface UpdateBankAccountRequest {
  name: string;
  initialBalance: number;
  currentBalance: number;
}

export interface BankAccountTransferRequest {
  bankAccountOrigin: string;
  bankAccountDestination: string;
  amount: number;
  description: string;
}

export interface CreatePaymentMethodRequest {
  description: string;
  bankAccountId: string;
  isForPayment: boolean;
  isForPayout: boolean;
}

export interface UpdatePaymentMethodRequest {
  description: string;
  bankAccountId: string;
  isForPayment: boolean;
  isForPayout: boolean;
}

// Accounts Payable
export type AccountPayableRecurrence = 'once' | 'daily' | 'weekly' | 'monthly' | 'yearly';
export type AccountPayableStatus = 'pending' | 'paid' | 'cancelled' | 'overdue';
export type RecurrenceScope = 'onlyThis' | 'nextOnes' | 'all';

export interface AccountPayableHistory {
  id: string;
  accountPayableId: string;
  description: string;
  createdAt: string;
}

export interface AccountPayable {
  id: string;
  payeeId: string;
  bankAccountId: string;
  paymentMethodId: string;
  amount: number; // May be cents depending on backend; UI handles mask
  dueDate: string; // ISO
  paymentDate: string | null; // ISO or null
  recurrence: AccountPayableRecurrence;
  recurrenceEndDate?: string | null;
  recurrenceGroupIdentifier?: string | null;
  description: string;
  status: AccountPayableStatus;
  createdAt: string;
  updatedAt: string;
  histories?: AccountPayableHistory[];
}

export interface UpdateAccountPayableRequest {
  payeeId: string;
  bankAccountId: string;
  paymentMethodId: string;
  amount: number; // send in cents
  dueDate: string;
  paymentDate: string | null;
  recurrence?: AccountPayableRecurrence;
  recurrenceEndDate?: string | null;
  recurrenceGroupIdentifier?: string | null;
  description: string;
  status?: AccountPayableStatus;
  recurrenceScope?: RecurrenceScope;
}

export interface CreateAccountPayableRequest extends UpdateAccountPayableRequest {}

// Accounts Receivable
export type AccountReceivableRecurrence = AccountPayableRecurrence;
export type AccountReceivableStatus = 'pending' | 'received' | 'cancelled' | 'overdue';

export interface AccountReceivableHistory {
  id: string;
  accountReceivableId: string;
  description: string;
  createdAt: string;
}

export interface AccountReceivable {
  id: string;
  payerId: string;
  bankAccountId: string;
  paymentMethodId: string;
  amount: number;
  dueDate: string;
  receiptDate: string | null;
  recurrence: AccountReceivableRecurrence;
  recurrenceEndDate?: string | null;
  recurrenceGroupIdentifier?: string | null;
  description: string;
  status: AccountReceivableStatus;
  createdAt: string;
  updatedAt: string;
  histories?: AccountReceivableHistory[];
}

export interface UpdateAccountReceivableRequest {
  payerId: string;
  bankAccountId: string;
  paymentMethodId: string;
  amount: number; // send in cents
  dueDate: string;
  receiptDate: string | null;
  recurrence?: AccountReceivableRecurrence;
  recurrenceEndDate?: string | null;
  recurrenceGroupIdentifier?: string | null;
  description: string;
  status?: AccountReceivableStatus;
  recurrenceScope?: RecurrenceScope;
}

export interface CreateAccountReceivableRequest extends UpdateAccountReceivableRequest {}

// Bank Account Transactions
export type TransactionType = 'credit' | 'debit';
export type TransactionSourceType = 'account_payable' | 'account_receivable' | 'refund' | 'adjustment' | 'transfer';

export interface BankTransaction {
  id: string;
  occurredAt: string;
  description: string;
  amount: number; // in cents
  balanceAfter: number; // in cents
  bankAccountId: string;
  sourceId: string;
  sourceType: TransactionSourceType;
  type: TransactionType;
}

// Analytics
export interface ProjectedCashflowItem {
  date: string;
  balance: number; // in cents
}

export interface AnalyticsSummary {
  totalPayables: number; // in cents
  totalReceivables: number; // in cents
  netResult: number; // in cents
  paidPercentage: number;
  receivedPercentage: number;
  /** Valor investido em caixinhas (vault) até o momento, em centavos */
  totalInvestments?: number;
  /** Soma do resultado líquido com os valores investidos (em centavos) */
  finalTotal?: number;
  projectedCashflow?: ProjectedCashflowItem[]; // optional - may not be present in response
}

export interface CashflowItem {
  period: string; // "2025-01" or "2025-01-15" depending on groupBy
  inflow: number; // in cents
  outflow: number; // in cents
  net: number; // in cents
}

// Investments
export type InvestmentType = 'credit' | 'debit' | 'yield';
export interface CreateInvestmentRequest {
  bankAccountId: string;
  amount: number; // in cents, must be > 0
  occurredAt: string; // ISO date
  type: InvestmentType; // credit => investimento, debit => resgate
}

export interface Investment {
  id: string;
  bankAccountId: string;
  type: InvestmentType;
  amount: number; // in cents
  balanceAfter?: number; // in cents
  occurredAt: string; // ISO
  createdAt: string; // ISO
}

// Users
export interface UserAccount {
  id: string;
  number: number;
  name: string;
}

export interface User {
  id: string;
  name: string;
  username: string;
  accountId: string;
  isActive?: boolean;
  active?: boolean;
  twoFactorEnabled?: boolean;
  mfaEnabled?: boolean;
  createdAt: string;
  updatedAt: string;
  account: UserAccount;
}

// Expense control (centavos)
export interface ExpenseCategory {
  id: string;
  accountId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateExpenseCategoryRequest {
  name: string;
}

export interface UpdateExpenseCategoryRequest {
  name: string;
}

export interface Expense {
  id: string;
  accountId: string;
  categoryId: string;
  amount: number;
  date: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateExpenseRequest {
  categoryId: string;
  amount: number;
  date: string;
  description?: string;
}

export interface UpdateExpenseRequest {
  categoryId?: string;
  amount?: number;
  date?: string;
  description?: string;
}

export interface ExpenseReportCategoryRow {
  categoryId: string;
  categoryName: string;
  total: number;
}

export interface ExpenseMonthlyReport {
  year: number;
  month: number;
  totalAmount: number;
  categories: ExpenseReportCategoryRow[];
}

// Audit logs
export enum AuditAction {
  UserAuthenticated = 'USER_AUTHENTICATED',
  AuthenticationFailed = 'AUTHENTICATION_FAILED',
  TwoFactorChallengeCreated = 'TWO_FACTOR_CHALLENGE_CREATED',
  TwoFactorChallengeFailed = 'TWO_FACTOR_CHALLENGE_FAILED',
  TwoFactorChallengeAuthenticated = 'TWO_FACTOR_CHALLENGE_AUTHENTICATED',
  UpdateUserProfile = 'UPDATE_USER_PROFILE',
  ChangeTwoFactorAuthenticationSetting = 'CHANGE_TWO_FACTOR_SETTING',
  CreatePayee = 'CREATE_PAYEE',
  UpdatePayee = 'UPDATE_PAYEE',
  CreatePayer = 'CREATE_PAYER',
  UpdatePayer = 'UPDATE_PAYER',
  CreateUser = 'CREATE_USER',
  UpdateUserStatus = 'UPDATE_USER_STATUS',
  CreateAccountInvite = 'CREATE_ACCOUNT_INVITE',
  CreateBankAccount = 'CREATE_BANK_ACCOUNT',
  UpdateBankAccount = 'UPDATE_BANK_ACCOUNT',
  TransferBetweenBankAccounts = 'TRANSFER_BETWEEN_BANK_ACCOUNTS',
  CreatePaymentMethod = 'CREATE_PAYMENT_METHOD',
  UpdatePaymentMethod = 'UPDATE_PAYMENT_METHOD',
  CreateAccountPayable = 'CREATE_ACCOUNT_PAYABLE',
  UpdateAccountPayable = 'UPDATE_ACCOUNT_PAYABLE',
  DeleteAccountPayable = 'DELETE_ACCOUNT_PAYABLE',
  MarkAsPaidAccountPayable = 'MARK_AS_PAID_ACCOUNT_PAYABLE',
  RefundAccountPayable = 'REFUND_ACCOUNT_PAYABLE',
  CreateAccountReceivable = 'CREATE_ACCOUNT_RECEIVABLE',
  UpdateAccountReceivable = 'UPDATE_ACCOUNT_RECEIVABLE',
  DeleteAccountReceivable = 'DELETE_ACCOUNT_RECEIVABLE',
  MarkAsReceivedAccountReceivable = 'MARK_AS_RECEIVED_ACCOUNT_RECEIVABLE',
  RefundAccountReceivable = 'REFUND_ACCOUNT_RECEIVABLE',
  CreateInvestment = 'CREATE_INVESTMENT',
  CreateExpenseCategory = 'CREATE_EXPENSE_CATEGORY',
  UpdateExpenseCategory = 'UPDATE_EXPENSE_CATEGORY',
  DeleteExpenseCategory = 'DELETE_EXPENSE_CATEGORY',
  CreateExpense = 'CREATE_EXPENSE',
  UpdateExpense = 'UPDATE_EXPENSE',
  DeleteExpense = 'DELETE_EXPENSE',
}

export const AUDIT_ACTION_LABELS: Record<AuditAction, string> = {
  [AuditAction.UserAuthenticated]: 'Autenticação realizada',
  [AuditAction.AuthenticationFailed]: 'Falha na autenticação',
  [AuditAction.TwoFactorChallengeCreated]: 'Desafio 2FA criado',
  [AuditAction.TwoFactorChallengeFailed]: 'Falha no desafio 2FA',
  [AuditAction.TwoFactorChallengeAuthenticated]: '2FA autenticado',
  [AuditAction.UpdateUserProfile]: 'Perfil atualizado',
  [AuditAction.ChangeTwoFactorAuthenticationSetting]: 'Configuração 2FA alterada',
  [AuditAction.CreatePayee]: 'Favorecido criado',
  [AuditAction.UpdatePayee]: 'Favorecido atualizado',
  [AuditAction.CreatePayer]: 'Pagador criado',
  [AuditAction.UpdatePayer]: 'Pagador atualizado',
  [AuditAction.CreateUser]: 'Usuário criado',
  [AuditAction.UpdateUserStatus]: 'Status do usuário atualizado',
  [AuditAction.CreateAccountInvite]: 'Convite de conta criado',
  [AuditAction.CreateBankAccount]: 'Conta bancária criada',
  [AuditAction.UpdateBankAccount]: 'Conta bancária atualizada',
  [AuditAction.TransferBetweenBankAccounts]: 'Transferência entre contas',
  [AuditAction.CreatePaymentMethod]: 'Método de pagamento criado',
  [AuditAction.UpdatePaymentMethod]: 'Método de pagamento atualizado',
  [AuditAction.CreateAccountPayable]: 'Conta a pagar criada',
  [AuditAction.UpdateAccountPayable]: 'Conta a pagar atualizada',
  [AuditAction.DeleteAccountPayable]: 'Conta a pagar excluída',
  [AuditAction.MarkAsPaidAccountPayable]: 'Conta a pagar marcada como paga',
  [AuditAction.RefundAccountPayable]: 'Conta a pagar estornada',
  [AuditAction.CreateAccountReceivable]: 'Conta a receber criada',
  [AuditAction.UpdateAccountReceivable]: 'Conta a receber atualizada',
  [AuditAction.DeleteAccountReceivable]: 'Conta a receber excluída',
  [AuditAction.MarkAsReceivedAccountReceivable]: 'Conta a receber marcada como recebida',
  [AuditAction.RefundAccountReceivable]: 'Conta a receber estornada',
  [AuditAction.CreateInvestment]: 'Investimento criado',
  [AuditAction.CreateExpenseCategory]: 'Categoria de gasto criada',
  [AuditAction.UpdateExpenseCategory]: 'Categoria de gasto atualizada',
  [AuditAction.DeleteExpenseCategory]: 'Categoria de gasto excluída',
  [AuditAction.CreateExpense]: 'Gasto criado',
  [AuditAction.UpdateExpense]: 'Gasto atualizado',
  [AuditAction.DeleteExpense]: 'Gasto excluído',
};

export interface AuditLogFieldChange {
  fieldName: string;
  oldValue: string;
  newValue: string;
}

export interface AuditLog {
  id: string;
  userId: string;
  action: AuditAction;
  description: string;
  metadata: string | Record<string, unknown> | AuditLogFieldChange[] | null;
  createdAt: string;
}
